import { z } from 'zod'

// --- Types ---

export interface FieldSchemaConfig {
  /** Field name identifier */
  name: string
  /** Human-readable label for error messages */
  label: string
  /** Whether the field is required */
  required?: boolean
  /** Min length constraint */
  minLength?: number
  /** Max length constraint */
  maxLength?: number
  /** Regex pattern string for validation */
  regex?: string
  /** Human-readable example for regex error (ex. BAQCR3546H) */
  regexExample?: string
  /** Custom error message for regex failure */
  regexErrorMessage?: string
}

// --- Regex → human-readable format helpers ---

/**
 * Expands the body of a regex character class (the text between [ and ]) into
 * the alphanumeric characters it allows. Handles ranges of any span
 * ("1-9", "A-F", "0-9") and \d / \w shorthands, so "[1-9]" is treated as
 * digits just like "[0-9]". Returns null for negated classes ("[^...]"),
 * which cannot be described as a simple set.
 */
export function expandCharClass(cls: string): string[] | null {
  if (cls.startsWith('^')) return null
  const body = cls.replace(/\\d/g, '0-9').replace(/\\w/g, 'A-Za-z0-9_')
  const chars = new Set<string>()
  for (let i = 0; i < body.length; i++) {
    const start = body[i]
    if (body[i + 1] === '-' && i + 2 < body.length) {
      const end = body[i + 2]
      for (let c = start.charCodeAt(0); c <= end.charCodeAt(0); c++) {
        chars.add(String.fromCharCode(c))
      }
      i += 2
    } else {
      chars.add(start)
    }
  }
  return [...chars].filter(c => /[A-Za-z0-9]/.test(c))
}

/** Classifies a character class as digits, letters, letters/digits, or generic. */
export function describeCharClass(cls: string): string {
  const chars = expandCharClass(cls)
  if (!chars || !chars.length) return 'characters'
  const hasDigits = chars.some(c => /[0-9]/.test(c))
  const hasLetters = chars.some(c => /[A-Za-z]/.test(c))
  if (hasDigits && hasLetters) return 'alphanumeric characters'
  if (hasDigits) return 'digits'
  return 'letters'
}

/**
 * Describes a single (alternation-free) regex branch in plain English, e.g.
 *   "[0-9]{9}[A-Z]"      -> "9 digits followed by 1 letter"
 *   "[1-9][0-9]{12}"     -> "13 digits (cannot start with 0)"
 *   "[A-Z]{5}[0-9]{4}[A-Z]" -> "5 letters followed by 4 digits followed by 1 letter"
 * Returns '' when the branch is not a simple sequence of quantified char classes.
 */
function describeRegexBranch(branch: string): string {
  // Strip any leading/trailing anchors on the branch — backend regexes often
  // anchor each alternation branch itself (e.g. "^[0-9]{9}[A-Z]$|^...$").
  const body = branch.trim().replace(/^\^+/, '').replace(/\$+$/, '')
  // Match tokens like [A-Z]{5}, [0-9]{9,10}, [A-Z] (implicit {1}).
  const tokenRe = /\[([^\]]+)\]\{(\d+)(?:,(\d+))?\}|\[([^\]]+)\]/g
  // Consecutive tokens of the same kind are merged so "[1-9][0-9]{12}" reads
  // as "13 digits" rather than "1 digit followed by 12 digits".
  type Part = { noun: string; min: number; max: number; noLeadingZero: boolean }
  const parts: Part[] = []
  let match: RegExpExecArray | null
  let lastIndex = 0
  while ((match = tokenRe.exec(body)) !== null) {
    // Bail out if there is content we did not understand between tokens.
    if (match.index !== lastIndex) return ''
    const cls = match[1] ?? match[4] ?? ''
    const min = match[2] ? parseInt(match[2], 10) : 1
    const max = match[3] ? parseInt(match[3], 10) : min

    // Classify by the characters the class actually allows, so "[1-9]" is
    // "digits" (not "characters") and "[A-F]" is "letters".
    const noun = describeCharClass(cls)
    const allowed = expandCharClass(cls) ?? []
    // A digit class without 0 at the very start of the value (e.g. "[1-9]")
    // means the value cannot start with 0 — worth telling the user.
    const excludesZero =
      noun === 'digits' && !allowed.includes('0') && parts.length === 0

    const prev = parts[parts.length - 1]
    if (prev && prev.noun === noun) {
      prev.min += min
      prev.max += max
    } else {
      parts.push({ noun, min, max, noLeadingZero: excludesZero })
    }
    lastIndex = tokenRe.lastIndex
  }
  if (!parts.length || lastIndex !== body.length) return ''

  const text = parts
    .map(({ noun, min, max }) => {
      const count = max !== min ? `${min}-${max}` : `${min}`
      // Singularise when exactly one is required ("1 letter" not "1 letters").
      const label =
        min === 1 && max === 1
          ? noun.replace(/characters$/, 'character').replace(/s$/, '')
          : noun
      return `${count} ${label}`
    })
    .join(' followed by ')
  return parts[0].noLeadingZero ? `${text} (cannot start with 0)` : text
}

/**
 * Builds a human-readable description of ALL accepted formats for a regex,
 * splitting on top-level `|` alternations and joining the branches with " OR "
 * so the user can see every valid pattern. Returns '' when the regex cannot be
 * described (callers then fall back to the plain example-based message).
 *
 * e.g. "[0-9]{9}[A-Z]|[0-9]{10}[A-Z]" ->
 *      "9 digits followed by 1 letter OR 10 digits followed by 1 letter"
 */
export function describeRegexFormats(regex: string): string {
  const stripped = regex.trim().replace(/^\^/, '').replace(/\$$/, '')
  // Unwrap a single outer (?:...) / (...) group so grouped alternations work.
  const unwrapped = /^\((?:\?:)?(.*)\)$/.exec(stripped)?.[1] ?? stripped
  const branches = unwrapped.split('|')
  const described = branches.map(b => describeRegexBranch(b))
  if (described.some(d => !d)) return ''
  return described.join(', or ')
}

// --- Schema Builder ---

/**
 * Builds a Zod string schema from a field configuration.
 * Reusable across any form that needs configurable validation.
 */
export function buildFieldSchema(config: FieldSchemaConfig): z.ZodTypeAny {
  let schema = z.string()

  if (config.required) {
    schema = schema.min(1, { message: `${config.label} is required` })
  }

  if (config.minLength) {
    schema = schema.min(config.minLength, {
      message: `${config.label} must be at least ${config.minLength} characters`,
    })
  }

  if (config.maxLength) {
    schema = schema.max(config.maxLength, {
      message: `${config.label} cannot exceed ${config.maxLength} characters`,
    })
  }

  if (config.regex) {
    // Wrap in a non-capturing group so the ^...$ anchors apply to EVERY
    // alternation branch (e.g. "A|B" must become "^(?:A|B)$", not "^A|B$"
    // which only anchors the first/last branch).
    const anchored = new RegExp(`^(?:${config.regex})$`)

    // Describe every accepted format (one per alternation branch) so the user
    // sees all valid patterns, e.g. "9 digits followed by 1 letter OR 10
    // digits followed by 1 letter". Falls back to the example-based message
    // when the regex is too complex to describe.
    const formats = describeRegexFormats(config.regex)
    // Turn the raw example ("ex. 123456789A") into a natural "for example"
    // phrase; keep whatever the caller passed if it is already friendly.
    const examplePhrase = config.regexExample
      ? ` For example: ${config.regexExample.replace(/^ex\.\s*/i, '')}.`
      : ''
    const errorMsg =
      config.regexErrorMessage ||
      (formats
        ? `Please enter a valid ${config.label}. It should be ${formats}.${examplePhrase}`
        : `Please enter a valid ${config.label}.${examplePhrase}`)

    schema = schema.regex(anchored, {
      message: errorMsg,
    })
  }

  return schema
}

/**
 * Builds a Zod object schema from an array of field configurations.
 * Use this to create dynamic, configurable form schemas at runtime.
 *
 * @example
 * const schema = buildFormSchema([
 *   { name: 'entityName', label: 'Entity name', required: true, maxLength: 75 },
 *   { name: 'country', label: 'Country', required: true },
 *   { name: 'uin', label: 'PAN', required: true, regex: '[A-Z]{4}[0-9]{5}[A-Z]', regexExample: 'ex. BAQCR3546H' },
 * ])
 * const result = schema.safeParse(formData)
 */
export function buildFormSchema(
  fields: FieldSchemaConfig[]
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {}

  for (const field of fields) {
    shape[field.name] = buildFieldSchema(field)
  }

  return z.object(shape)
}

/**
 * Validates form data against a dynamically built schema.
 * Returns a map of field name -> error message, or null if valid.
 */
export function validateWithSchema(
  fields: FieldSchemaConfig[],
  data: Record<string, unknown>
): Record<string, string> | null {
  const schema = buildFormSchema(fields)
  const result = schema.safeParse(data)

  if (result.success) return null

  const errors: Record<string, string> = {}
  for (const issue of result.error.issues) {
    const fieldName = issue.path[0] as string
    if (!errors[fieldName]) {
      errors[fieldName] = issue.message
    }
  }
  return errors
}

/**
 * Validates a single field value against its schema config.
 * Useful for real-time (on-change) validation.
 * Returns error message or null if valid.
 */
export function validateField(
  config: FieldSchemaConfig,
  value: string
): string | null {
  const schema = buildFieldSchema(config)
  const result = schema.safeParse(value)
  if (result.success) return null
  return result.error.issues[0]?.message || null
}
